import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE } from '../config';
import {
  unitsPerFormat,
  formatsToUnits,
  unitsToFormats,
  describeFormats,
  describeQuantity,
} from '../utils/formatUnits';
import '../styles/Solicitudes.css';

const REASONS = [
  { value: 'falta_stock', label: 'Falta de stock' },
  { value: 'recuento', label: 'Recuento' },
  { value: 'error_pedido', label: 'Error en el pedido' },
  { value: 'otro', label: 'Otro' },
];

const Solicitudes = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);
  const { token } = useAuth();
  const navigate = useNavigate();

  const fetchRequests = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/quantity-requests/list/`, {
        headers: { Authorization: `Token ${token}` },
      });
      if (!response.ok) throw new Error('No se pudieron cargar las solicitudes');
      const data = await response.json();
      setRequests(data.requests);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchRequests();
    const interval = setInterval(fetchRequests, 20000);
    return () => clearInterval(interval);
  }, [fetchRequests]);

  // Lines whose format holds several units are approved in units (what the
  // approver counts) and saved in formats (what the WMS and Loken use).
  const draftFor = (req) => drafts[req.id] || {
    quantity: unitsPerFormat(req)
      ? String(formatsToUnits(req, req.requested_quantity))
      : String(req.requested_quantity),
    reason: 'falta_stock',
    note: '',
    confirmingOver: false,
  };

  // Takes the request, not just its id: without it the defaults cannot be
  // derived and editing the note would blank the quantity field.
  const setDraft = (req, patch) =>
    setDrafts((prev) => ({ ...prev, [req.id]: { ...draftFor(req), ...patch } }));

  const resolve = async (req, action, overConfirmed = false) => {
    const draft = draftFor(req);
    let approvedQuantity;

    if (action === 'approve') {
      const typed = Number(draft.quantity);
      if (String(draft.quantity).trim() === '' || Number.isNaN(typed)) {
        setError('La cantidad debe ser un número.');
        return;
      }
      if (unitsPerFormat(req) && !Number.isInteger(typed)) {
        setError('Ingresa unidades enteras.');
        return;
      }
      approvedQuantity = unitsPerFormat(req) ? unitsToFormats(req, typed) : typed;
      // Asked in the card, not with window.confirm, like the rest of the app.
      if (!overConfirmed && approvedQuantity > Number(req.ordered_quantity)) {
        setDraft(req, { confirmingOver: true });
        return;
      }
    }

    setSavingId(req.id);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/quantity-requests/resolve/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Token ${token}`,
        },
        body: JSON.stringify({
          request_id: req.id,
          action,
          approved_quantity: approvedQuantity,
          reason: draft.reason,
          resolution_note: draft.note,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        setError(data.error || 'No se pudo resolver la solicitud.');
        return;
      }
      await fetchRequests();
    } catch (err) {
      setError('No se pudo resolver la solicitud. Revisa tu conexión.');
    } finally {
      setSavingId(null);
    }
  };

  const waitingSince = (createdAt) => {
    const minutes = Math.floor((Date.now() - new Date(createdAt)) / 60000);
    if (minutes < 60) return `${minutes} min`;
    return `${Math.floor(minutes / 60)}h ${minutes % 60}min`;
  };

  return (
    <div className="solicitudes-container">
      <button className="solicitudes-back-button" onClick={() => navigate('/init')}>
        &#8592;
      </button>
      <h1 className="solicitudes-title">Solicitudes de Modificación</h1>

      {error && <div className="solicitudes-error">{error}</div>}
      {loading && <p>Cargando...</p>}
      {!loading && requests.length === 0 && (
        <p className="solicitudes-empty">No hay solicitudes pendientes.</p>
      )}

      <div className="solicitudes-grid">
        {requests.map((req) => {
          const draft = draftFor(req);
          const inUnits = Boolean(unitsPerFormat(req));
          return (
            <div key={req.id} className="solicitud-card">
              <h4>{req.po_number}</h4>
              <p><strong>Producto:</strong> {req.sku_name} ({req.sku_code})</p>
              <p><strong>Pedido:</strong> {describeQuantity(req, req.ordered_quantity)}</p>
              <p><strong>Solicitado:</strong> {describeQuantity(req, req.requested_quantity)}</p>
              <p><strong>Picker:</strong> {req.requested_by}</p>
              <p><strong>Esperando:</strong> {waitingSince(req.created_at)}</p>
              {req.note && <p><strong>Nota:</strong> {req.note}</p>}

              <label>
                Cantidad a aprobar ({inUnits ? 'unidades' : req.format_name || 'kg'}):
                <input
                  type="number"
                  min="0"
                  step={inUnits ? '1' : '0.001'}
                  value={draft.quantity}
                  onChange={(e) => setDraft(req, { quantity: e.target.value, confirmingOver: false })}
                />
              </label>
              {inUnits && draft.quantity !== '' && (
                <p className="solicitud-conversion">
                  = {describeFormats(req, unitsToFormats(req, draft.quantity))}
                </p>
              )}
              <label>
                Motivo:
                <select
                  value={draft.reason}
                  onChange={(e) => setDraft(req, { reason: e.target.value })}
                >
                  {REASONS.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </label>
              <label>
                Comentario (obligatorio al rechazar):
                <input
                  type="text"
                  value={draft.note}
                  onChange={(e) => setDraft(req, { note: e.target.value })}
                  placeholder="Mensaje para el picker"
                />
              </label>

              {draft.confirmingOver && (
                <div className="solicitud-warning">
                  <p>Supera lo pedido ({describeQuantity(req, req.ordered_quantity)}).</p>
                  <div className="solicitud-actions">
                    <button
                      type="button"
                      className="solicitud-approve"
                      disabled={savingId === req.id}
                      onClick={() => resolve(req, 'approve', true)}
                    >
                      Aprobar igual
                    </button>
                    <button
                      type="button"
                      className="solicitud-cancel"
                      onClick={() => setDraft(req, { confirmingOver: false })}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              )}

              <div className="solicitud-actions">
                <button
                  type="button"
                  className="solicitud-approve"
                  disabled={savingId === req.id}
                  onClick={() => resolve(req, 'approve')}
                >
                  {savingId === req.id ? 'Guardando...' : 'Aprobar'}
                </button>
                <button
                  type="button"
                  className="solicitud-reject"
                  disabled={savingId === req.id}
                  onClick={() => resolve(req, 'reject')}
                >
                  Rechazar
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Solicitudes;
